/**
 * Lead Comms — Firestore CRUD + subscriptions for call / meeting / email / note history.
 * Collection: leadComms. Staff CRM only (rules: isCrmWorkspaceUser).
 */

import { db } from './firebase';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import type { LeadComm, LeadCommAudience, LeadCommType } from '../types';
import { firestoreSnapshotListenError } from './firestoreListenDebug';

export const LEAD_COMMS_COLLECTION = 'leadComms';

const VALID_TYPES: LeadCommType[] = ['call', 'meeting', 'email', 'note'];

function parseType(raw: unknown): LeadCommType {
  return VALID_TYPES.includes(raw as LeadCommType) ? (raw as LeadCommType) : 'note';
}

function parseAudience(raw: unknown): LeadCommAudience {
  return raw === 'external' ? 'external' : 'internal';
}

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): LeadComm {
  const d = snap.data();
  return {
    id: snap.id,
    leadId: (d.leadId as string) || '',
    dealId: typeof d.dealId === 'string' && d.dealId ? d.dealId : undefined,
    projectId: typeof d.projectId === 'string' && d.projectId ? d.projectId : undefined,
    type: parseType(d.type),
    occurredAt: (d.occurredAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    participants: Array.isArray(d.participants)
      ? d.participants.filter((x): x is string => typeof x === 'string')
      : [],
    title: (d.title as string) || '',
    summary: typeof d.summary === 'string' ? d.summary : '',
    sourceLink: typeof d.sourceLink === 'string' && d.sourceLink ? d.sourceLink : undefined,
    source: typeof d.source === 'string' && d.source ? d.source : 'manual',
    externalId: typeof d.externalId === 'string' && d.externalId ? d.externalId : undefined,
    audience: parseAudience(d.audience),
    createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
    createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    updatedAt: (d.updatedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
  };
}

export type LeadCommInput = {
  leadId: string;
  type: LeadCommType;
  occurredAt: Date;
  title: string;
  summary?: string;
  participants?: string[];
  sourceLink?: string;
  source?: string;
  externalId?: string;
  audience?: LeadCommAudience;
  dealId?: string;
  projectId?: string;
  createdBy: string;
};

/** Real-time lead comms (newest first). */
export function subscribeLeadComms(
  leadId: string,
  callback: (comms: LeadComm[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, LEAD_COMMS_COLLECTION),
    where('leadId', '==', leadId),
    orderBy('occurredAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      firestoreSnapshotListenError('leadCommsService', `${LEAD_COMMS_COLLECTION}/lead/${leadId}`, err);
      onError?.(err as Error);
    },
  );
}

/**
 * Project Hub: show the same leadComms rows after conversion.
 * Prefers projectId stamp; also includes unstamped rows for the lead (no duplicate docs).
 */
export function subscribeLeadCommsForProject(
  opts: { projectId: string; leadId?: string | null },
  callback: (comms: LeadComm[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const { projectId, leadId } = opts;
  let byProject: LeadComm[] = [];
  let byLead: LeadComm[] = [];
  let unsubLead: Unsubscribe | null = null;

  const emit = () => {
    const map = new Map<string, LeadComm>();
    for (const c of byProject) {
      if (c.id) map.set(c.id, c);
    }
    for (const c of byLead) {
      if (!c.id) continue;
      // Prefer project-stamped rows; include unstamped lead rows so nothing is lost pre-link.
      if (!c.projectId || c.projectId === projectId) {
        map.set(c.id, c);
      }
    }
    const merged = Array.from(map.values()).sort(
      (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
    );
    callback(merged);
  };

  const qProject = query(
    collection(db, LEAD_COMMS_COLLECTION),
    where('projectId', '==', projectId),
    orderBy('occurredAt', 'desc'),
  );
  const unsubProject = onSnapshot(
    qProject,
    snap => {
      byProject = snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
      emit();
    },
    err => {
      firestoreSnapshotListenError(
        'leadCommsService',
        `${LEAD_COMMS_COLLECTION}/project/${projectId}`,
        err,
      );
      onError?.(err as Error);
    },
  );

  if (leadId) {
    unsubLead = subscribeLeadComms(
      leadId,
      rows => {
        byLead = rows;
        emit();
      },
      onError,
    );
  }

  return () => {
    unsubProject();
    unsubLead?.();
  };
}

export async function createLeadComm(input: LeadCommInput): Promise<string> {
  const ref = await addDoc(collection(db, LEAD_COMMS_COLLECTION), {
    leadId: input.leadId,
    dealId: input.dealId || null,
    projectId: input.projectId || null,
    type: input.type,
    occurredAt: input.occurredAt,
    participants: input.participants || [],
    title: input.title.trim(),
    summary: (input.summary || '').trim(),
    sourceLink: input.sourceLink?.trim() || null,
    source: (input.source || 'manual').trim().toLowerCase() || 'manual',
    externalId: input.externalId?.trim() || null,
    audience: input.audience === 'external' ? 'external' : 'internal',
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateLeadComm(
  id: string,
  updates: Partial<Omit<LeadCommInput, 'leadId' | 'createdBy'>>,
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (updates.type !== undefined) payload.type = updates.type;
  if (updates.occurredAt !== undefined) payload.occurredAt = updates.occurredAt;
  if (updates.title !== undefined) payload.title = updates.title.trim();
  if (updates.summary !== undefined) payload.summary = updates.summary.trim();
  if (updates.participants !== undefined) payload.participants = updates.participants;
  if (updates.sourceLink !== undefined) payload.sourceLink = updates.sourceLink?.trim() || null;
  if (updates.source !== undefined) payload.source = updates.source.trim().toLowerCase() || 'manual';
  if (updates.externalId !== undefined) payload.externalId = updates.externalId?.trim() || null;
  if (updates.audience !== undefined) {
    payload.audience = updates.audience === 'external' ? 'external' : 'internal';
  }
  if (updates.dealId !== undefined) payload.dealId = updates.dealId || null;
  if (updates.projectId !== undefined) payload.projectId = updates.projectId || null;
  await updateDoc(doc(db, LEAD_COMMS_COLLECTION, id), payload as DocumentData);
}

export async function deleteLeadComm(id: string): Promise<void> {
  await deleteDoc(doc(db, LEAD_COMMS_COLLECTION, id));
}

/** Stamp dealId onto all leadComms for a lead (in-place; used on lead→deal conversion). */
export async function linkLeadCommsToDeal(leadId: string, dealId: string): Promise<number> {
  if (!leadId || !dealId) return 0;
  const snap = await getDocs(
    query(collection(db, LEAD_COMMS_COLLECTION), where('leadId', '==', leadId)),
  );
  if (snap.empty) return 0;
  const batch = writeBatch(db);
  let n = 0;
  for (const d of snap.docs) {
    if (d.data().dealId === dealId) continue;
    batch.update(d.ref, { dealId, updatedAt: serverTimestamp() });
    n += 1;
  }
  if (n > 0) await batch.commit();
  return n;
}

/** Stamp projectId (and optional dealId) onto leadComms (in-place; used on deal→project). */
export async function linkLeadCommsToProject(
  leadId: string,
  projectId: string,
  dealId?: string | null,
): Promise<number> {
  if (!leadId || !projectId) return 0;
  const snap = await getDocs(
    query(collection(db, LEAD_COMMS_COLLECTION), where('leadId', '==', leadId)),
  );
  if (snap.empty) return 0;
  const batch = writeBatch(db);
  let n = 0;
  for (const d of snap.docs) {
    const data = d.data();
    const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
    let dirty = false;
    if (data.projectId !== projectId) {
      patch.projectId = projectId;
      dirty = true;
    }
    if (dealId && data.dealId !== dealId) {
      patch.dealId = dealId;
      dirty = true;
    }
    if (dirty) {
      batch.update(d.ref, patch as DocumentData);
      n += 1;
    }
  }
  if (n > 0) await batch.commit();
  return n;
}
